<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { PhStudent } from "@phosphor-icons/vue"
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Modal from '@pages/staff/components/StudentModal.vue'
import Table from '@pages/Shared/ui/Table.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import { flashToast } from "@/toast.js"
import { capitalize } from '@/helpers.js'
import userMale from '@/../images/user-male.png'
import userFemale from '@/../images/user-female.png'

const props = defineProps({
    students: Object,
    filters:  Object,
})

const openModal      = ref(false)
const modalStatus    = ref(null)
const modalTitle     = ref('')
const showPrompt     = ref(false)
const selectedStudent = ref(null)
const deleteId       = ref(null)
const sections       = ref([])

const filters = ref({ search: null, section: null, class: null, status: null, ...props.filters })

const columns = [
    { key: 'index',                   label: '#',             type: 'index'   },
    { key: 'admission',               label: 'Student ID',    type: 'slot'    },
    { key: 'avatar',                  label: 'Avatar',        type: 'slot'    },
    { key: 'name',                    label: 'Name',          type: 'slot'    },
    { key: 'email',                   label: 'Email'                          },
    { key: 'gender',                  label: 'Gender'                         },
    { key: 'dob',                     label: 'DOB'                            },
    { key: 'phone',                   label: 'Phone'                          },
    { key: 'address',                 label: 'Address'                        },
    { key: 'guardian',                label: 'Guardian'                       },
    { key: 'guardian_phone',          label: 'Guardian Phone'                 },
    { key: 'class_room.name',         label: 'Class'                          },
    { key: 'class_room.section.name', label: 'Section'                        },
    { key: 'status',                  label: 'Status',        type: 'slot'    },
    { key: 'actions',                 label: '',              type: 'actions' },
]

const page          = usePage()
const user          = computed(() => page.props?.auth?.user || {})
const can           = computed(() => page.props?.can || {})
const userClassRooms = computed(() => user.value?.class_rooms ?? [])

const title = computed(() => 'Manage Students')

const selectedClasses = computed(() => {
    if (!filters.value?.section) return []
    const section = sections.value.find(s => String(s.id) === String(filters.value.section))
    return section?.classes || []
})

watch(() => props.filters, (val) => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined)
    )
    router.get('/staff/manage-students', { ...cleaned, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const handleSearch = (value) => { filters.value.search = value; applyFilters() }

const handleFilter = (newFilters) => {
    const sectionChanged = Object.prototype.hasOwnProperty.call(newFilters, 'section') &&
                           newFilters.section !== filters.value.section
    filters.value = { ...filters.value, ...newFilters }
    if (sectionChanged) filters.value.class = null
    applyFilters()
}

const findStudent = (id) => props.students?.data?.find(s => s.id === id) ?? null

const editStudent = (id) => {
    selectedStudent.value = findStudent(id)
    modalTitle.value      = 'Edit Student'
    modalStatus.value     = 'edit'
    openModal.value       = true
}

const viewStudent = (id) => {
    selectedStudent.value = findStudent(id)
    modalTitle.value      = 'View Student'
    modalStatus.value     = 'view'
    openModal.value       = true
}

const openAddStudentModal = () => {
    selectedStudent.value = null
    modalTitle.value      = 'Add Student'
    modalStatus.value     = 'create'
    openModal.value       = true
}

const deleteStudent = (id) => { deleteId.value = id; showPrompt.value = true }

const confirmDelete = () => {
    router.delete(`/staff/manage-students/${deleteId.value}`, {
        onSuccess: (page) => { showPrompt.value = false; flashToast(page.props.flash) }
    })
}

const handleSubmit = (form) => {
    const options = {
        onSuccess: (page) => { openModal.value = false; flashToast(page.props.flash) }
    }
    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/staff/manage-students/${encodeURIComponent(form.id)}`, options)
    } else {
        form.post('/staff/manage-students', options)
    }
}

onMounted(async () => {
    if (can.value?.viewAnySection) {
        const { data } = await axios.get('/staff/manage-sections')
        sections.value = data.sections || []
        return
    }

    // Build sections from all assigned classRooms
    if (userClassRooms.value.length) {
        const sectionMap = {}
        userClassRooms.value.forEach(cls => {
            const sId = cls.section?.id
            if (!sId) return
            if (!sectionMap[sId]) {
                sectionMap[sId] = {
                    id:      sId,
                    name:    cls.section.name,
                    classes: [],
                }
            }
            sectionMap[sId].classes.push({ id: cls.id, name: cls.name })
        })
        sections.value = Object.values(sectionMap)
    }
})
</script>

<template>
    <Head title="Manage Students" />
    <Layout>

        <Prompt
            title="student"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            :status="modalStatus"
            :sections="sections"
            :student="selectedStudent"
            @submitted="handleSubmit"
        />

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">{{ title }}</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all students</p>
            </div>
            <button
                v-if="can.createStudent"
                @click="openAddStudentModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhStudent :size="20" />
                Add Student
            </button>
        </div>

        <Table
            :data="students"
            :columns="columns"
            variant="staff"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            :filters="[
                {
                    key: 'section', label: 'Section',
                    options: sections.map(s => ({ label: s.name, value: s.id })),
                    value: filters.section,
                },
                {
                    key: 'class', label: 'Class',
                    options: selectedClasses.map(c => ({ label: c.name, value: c.id })),
                    value: filters.class,
                    disabled: !filters.section,
                },
                {
                    key: 'status', label: 'Status',
                    options: [
                        { label: 'Active',    value: 'active'    },
                        { label: 'Inactive',  value: 'inactive'  },
                        { label: 'Graduated', value: 'graduated' },
                    ],
                    value: filters.status,
                },
            ]"
            @search="handleSearch"
            @filter="handleFilter"
            @view="viewStudent"
            @edit="editStudent"
            @delete="deleteStudent"
        >
            <template #admission="{ row }">
                <span class="whitespace-nowrap text-sm text-gray-700">{{ row.id }}</span>
            </template>

            <template #avatar="{ row }">
                <img
                    class="w-9 h-9 rounded-full object-cover ring-2 ring-gray-100"
                    :src="row.avatar
                        ? `/storage/${row.avatar}`
                        : (row.gender === 'Male' ? userMale : userFemale)"
                    :alt="`${row.first_name} ${row.last_name}`"
                />
            </template>

            <template #name="{ row }">
                <span class="whitespace-nowrap text-sm font-medium text-gray-800">
                    {{ row.first_name }} {{ row.last_name }}
                    <span v-if="row.other_name" class="text-gray-800">{{ row.other_name }}</span>
                </span>
            </template>

            <template #status="{ row }">
                <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    :class="
                        row.status?.toLowerCase() === 'active'    ? 'bg-green-50 text-green-700'   :
                        row.status?.toLowerCase() === 'inactive'  ? 'bg-orange-50 text-orange-600' :
                        row.status?.toLowerCase() === 'graduated' ? 'bg-primary-50 text-primary-700'     :
                        'bg-red-50 text-red-600'
                    "
                >
                    <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="
                            row.status?.toLowerCase() === 'active'    ? 'bg-green-500'  :
                            row.status?.toLowerCase() === 'inactive'  ? 'bg-orange-400' :
                            row.status?.toLowerCase() === 'graduated' ? 'bg-primary-500'   :
                            'bg-red-500'
                        "
                    ></span>
                    {{ capitalize(row.status) }}
                </span>
            </template>
        </Table>

    </Layout>
</template>